<template>
  <v-card height='100%' :flat="nonCard" class="py-0 my-0">
    <v-card-title class="py-0 my-0">
      <v-checkbox v-model="localActiveValue" v-if="isActive"></v-checkbox>
      <v-card-subtitle class="py-0">
        {{ header }}
      </v-card-subtitle>
      <template v-if="timeFirstRow">
        <v-row no-gutters>

          <v-col cols="12" md="6">
            <span class="caption font-weight-bold">{{
                $vuetify.lang.t('$vuetify.map.managerMap.filterHeaders.lowerBound')
              }}</span>
            <v-row no-gutters>
              <v-col>
                <date-filter :value="start.date" :max="max" :min="min" type="start" :is-active="activeValue"
                             @date-filter-changed="changeDate"></date-filter>
              </v-col>
              <v-col>
                <time-filter type="start" ref="startTimeFilter" :disabled="!start.date" :is-active="activeValue"
                             @time-filter-changed="changeTime"></time-filter>

              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="6" class="px-md-2">
            <span class="caption font-weight-bold">{{
                $vuetify.lang.t('$vuetify.map.managerMap.filterHeaders.upperBound')
              }}</span>
            <v-row no-gutters>
              <v-col>
                <date-filter :value="finish.date" :max="max" :min="min" type="finish" :is-active="activeValue"
                             @date-filter-changed="changeDate"></date-filter>
              </v-col>
              <v-col>
                <time-filter type="finish" ref="finishTimeFilter" :disabled="!finish.date" :is-active="activeValue"
                             @time-filter-changed="changeTime"></time-filter>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
      <v-spacer></v-spacer>
      <v-btn icon @click="clearData">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on">{{ !reset ? filterRemoveIcon : filterResetIcon }}</v-icon>
          </template>
          <span>
            {{
              $vuetify.lang.t(!reset ? '$vuetify.map.tableFilter.clear' : '$vuetify.map.tableFilter.reset')
            }}
          </span>
        </v-tooltip>
      </v-btn>
    </v-card-title>
    <v-card-text class="py-0 my-0">
      <template v-if="!isTimeFirst">
        <span class="caption">{{ $vuetify.lang.t('$vuetify.map.managerMap.filterHeaders.lowerBound') }}</span>
        <v-row no-gutters>
          <v-col cols="12" md="6">
            <date-filter :value="start.date" :max="max" :min="min" type="start"
                         @date-filter-changed="changeDate"></date-filter>
          </v-col>
          <v-col cols="12" md="6">
            <time-filter type="start" ref="startTimeFilter" :disabled="!start.date"
                         @time-filter-changed="changeTime"></time-filter>
          </v-col>
        </v-row>
        <span class="caption">{{ $vuetify.lang.t('$vuetify.map.managerMap.filterHeaders.upperBound') }}</span>
        <v-row no-gutters class="py-0">
          <v-col cols="12" md="6">
            <date-filter :value="finish.date" :max="max" :min="min" type="finish"
                         @date-filter-changed="changeDate"></date-filter>
          </v-col>
          <v-col cols="12" md="6">
            <time-filter type="finish" ref="finishTimeFilter" :disabled="!finish.date"
                         @time-filter-changed="changeTime"></time-filter>
          </v-col>
        </v-row>
      </template>

      <template v-else>
        <template v-if="!timeFirstRow">
          <span class="caption font-weight-bold">{{
              $vuetify.lang.t('$vuetify.map.managerMap.filterHeaders.timeRange')
            }}</span>
          <v-row no-gutters>
            <v-col cols="12" md="6">
              <time-filter type="start" ref="startTimeFilter" :disabled="!start.date" :is-active="activeValue"
                           @time-filter-changed="changeTime"></time-filter>
            </v-col>
            <v-col cols="12" md="6">
              <time-filter type="finish" ref="finishTimeFilter" :disabled="!finish.date" :is-active="activeValue"
                           @time-filter-changed="changeTime"></time-filter>
            </v-col>
          </v-row>
          <span class="caption font-weight-bold">{{
              $vuetify.lang.t('$vuetify.map.managerMap.filterHeaders.dateRange')
            }}</span>
          <v-row no-gutters>
            <v-col cols="12" md="6">
              <date-filter :value="start.date" :max="max" :min="min" type="start" :is-active="activeValue"
                           @date-filter-changed="changeDate"></date-filter>
            </v-col>
            <v-col cols="12" md="6">
              <date-filter :value="finish.date" :max="max" :min="min" type="finish" :is-active="activeValue"
                           @date-filter-changed="changeDate"></date-filter>
            </v-col>
          </v-row>
        </template>
      </template>

    </v-card-text>
  </v-card>
</template>

<script>
import DateFilter from '../../map/tableFilters/utils/dateFilter'
import TimeFilter from '../../map/tableFilters/utils/timeFilter'
import { defaultChartDatetimeState, defaultDatetimeFilterState } from '@/utils/filters'
import { mdiCloseBoxMultiple, mdiFilter, mdiFilterRemove } from '@mdi/js'
import { getCurrentTime } from '@/utils/time'

const getDefaultState = (data, isTrim) => {
  return isTrim ? defaultDatetimeFilterState(data) : defaultChartDatetimeState(data)
}

const { _currentYear, _currentMonth, _lastDay } = getCurrentTime()

const getDefaultTimeRangeFilter = () => ({
  start: defaultChartDatetimeState(`${_currentYear}-${_currentMonth}-01`),
  finish: defaultChartDatetimeState(`${_currentYear}-${_currentMonth}-${_lastDay}`)
})

export default {
  name: 'datetimeChartFilter',
  components: { TimeFilter, DateFilter },
  props: {
    header: String,
    min: {
      type: String,
      default: ''
    },
    max: {
      type: String,
      default: ''
    },
    startDate: {
      type: String,
      default: ''
    },
    finishDate: {
      type: String,
      default: ''
    },
    trimDate: {
      type: Boolean,
      default: true
    },
    reset: {
      type: Boolean,
      default: false
    },
    nonCard: {
      type: Boolean,
      default: true
    },
    isActive: {
      type: Boolean,
      default: false
    },
    activeValue: {
      type: Boolean,
      default: false
    },
    isTimeFirst: {
      type: Boolean,
      default: false
    },
    timeFirstRow: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    localActiveValue: {
      get () {
        return this.activeValue
      },
      set (value) {
        this.$emit('active-value-changed', value)
      }
    }
  },
  data () {
    return {
      start: getDefaultState(this.startDate, this.trimDate),
      finish: getDefaultState(this.finishDate, this.trimDate),

      filterIcon: mdiFilter,
      filterRemoveIcon: mdiFilterRemove,
      filterResetIcon: mdiCloseBoxMultiple
    }
  },
  methods: {
    clearData (emit = true) {
      const states = ['start', 'finish']
      for (const state of states) {
        this[state] = getDefaultState(!this.isTimeFirst ? this[`${state}Date`] : getDefaultTimeRangeFilter()[state]['date'], this.trimDate)
        if (this.$refs.hasOwnProperty(`${state}TimeFilter`)) {
          this.$refs[`${state}TimeFilter`].clearState()
        }
      }

      if (emit) {
        this.$emit('datetime-cleared')
      }
    },
    changeDate (data) {
      this[data.type].date = data.value
      this.$emit('datetime-changed', { ...data, mode: 'date' })
    },
    changeTime (data) {
      this[data.type].time = data.value
      this.$emit('datetime-changed', { ...data, mode: 'time' })
    }
  }
}
</script>

<style scoped>

</style>
